/**
 * Global duration formatting utilities.
 *
 * Converts raw hours or seconds into human-readable durations.
 *   Verbose : "25 days, 3 hours, 0 minutes"
 *   Compact : "25d 3h 0m"
 */

/**
 * Format a decimal-hours value into a readable duration string.
 *
 * @param {number|null|undefined} hours - Decimal hours (e.g. 603.5)
 * @param {object}  [opts]
 * @param {boolean} [opts.compact=false]   - Use short units (d/h/m)
 * @param {boolean} [opts.showDays=true]   - Roll hours into days when ≥ 24
 * @param {string}  [opts.fallback='—']    - Returned for null / undefined / NaN
 * @returns {string}
 */
export const formatDurationHours = (hours, { t, compact = false, showDays = true, fallback = '—' } = {}) => {
  if (hours === null || hours === undefined || Number.isNaN(hours)) return fallback;

  const totalMinutes = Math.round(Math.max(0, hours) * 60);

  let days = 0;
  let remainingMinutes = totalMinutes;

  if (showDays) {
    days = Math.floor(remainingMinutes / (24 * 60));
    remainingMinutes %= 24 * 60;
  }

  const hrs = Math.floor(remainingMinutes / 60);
  const mins = remainingMinutes % 60;

  if (compact) {
    const parts = [];
    if (days > 0) parts.push(`${days}${t ? t('shared.day_short', 'd') : 'd'}`);
    parts.push(`${hrs}${t ? t('shared.hour_short', 'h') : 'h'}`);
    parts.push(`${mins}${t ? t('shared.minute_short', 'm') : 'm'}`);
    return parts.join(' ');
  }

  // Verbose
  const parts = [];
  if (days > 0) {
    const unit = t ? (days === 1 ? t('shared.day', 'day') : t('shared.days', 'days')) : (days === 1 ? 'day' : 'days');
    parts.push(`${days} ${unit}`);
  }
  
  const hrUnit = t ? (hrs === 1 ? t('shared.hour', 'hour') : t('shared.hours', 'hours')) : (hrs === 1 ? 'hour' : 'hours');
  parts.push(`${hrs} ${hrUnit}`);

  const minUnit = t ? (mins === 1 ? t('shared.minute', 'minute') : t('shared.minutes', 'minutes')) : (mins === 1 ? 'minute' : 'minutes');
  parts.push(`${mins} ${minUnit}`);
  
  return parts.join(', ');
};

/**
 * Format a total-seconds value into a readable duration string.
 * Useful for downtime metrics where the API returns seconds.
 *
 * @param {number|null|undefined} totalSeconds
 * @param {object}  [opts]
 * @param {function} [opts.t]              - Translation function
 * @param {boolean} [opts.compact=false]
 * @param {boolean} [opts.showDays=true]
 * @param {boolean} [opts.showSeconds=false]
 * @param {string}  [opts.fallback='—']
 * @returns {string}
 */
export const formatDurationSeconds = (totalSeconds, { t, compact = false, showDays = true, showSeconds = false, fallback = '—' } = {}) => {
  if (totalSeconds === null || totalSeconds === undefined || Number.isNaN(totalSeconds)) return fallback;

  let remaining = Math.max(0, Math.round(totalSeconds));

  let days = 0;
  if (showDays) {
    days = Math.floor(remaining / (24 * 3600));
    remaining %= 24 * 3600;
  }

  const hrs = Math.floor(remaining / 3600);
  remaining %= 3600;
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;

  if (compact) {
    const parts = [];
    if (days > 0) parts.push(`${days}${t ? t('shared.day_short', 'd') : 'd'}`);
    parts.push(`${hrs}${t ? t('shared.hour_short', 'h') : 'h'}`);
    parts.push(`${mins}${t ? t('shared.minute_short', 'm') : 'm'}`);
    if (showSeconds) parts.push(`${secs}${t ? t('shared.second_short', 's') : 's'}`);
    return parts.join(' ');
  }

  // Verbose
  const parts = [];
  if (days > 0) {
    const unit = t ? (days === 1 ? t('shared.day', 'day') : t('shared.days', 'days')) : (days === 1 ? 'day' : 'days');
    parts.push(`${days} ${unit}`);
  }

  const hrUnit = t ? (hrs === 1 ? t('shared.hour', 'hour') : t('shared.hours', 'hours')) : (hrs === 1 ? 'hour' : 'hours');
  parts.push(`${hrs} ${hrUnit}`);

  const minUnit = t ? (mins === 1 ? t('shared.minute', 'minute') : t('shared.minutes', 'minutes')) : (mins === 1 ? 'minute' : 'minutes');
  parts.push(`${mins} ${minUnit}`);

  if (showSeconds) {
    const secUnit = t ? (secs === 1 ? t('shared.second', 'second') : t('shared.seconds', 'seconds')) : (secs === 1 ? 'second' : 'seconds');
    parts.push(`${secs} ${secUnit}`);
  }

  return parts.join(', ');
};
